import { InputFile } from '@UI/InputNew/Input'
import Button from '@UI/Button'
import { useAddAlert } from '@hooks/useAddAlert'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useInputNew } from '@UI/InputNew/useInputNew'
import { usersAPI } from '@/api'
import classes from './index.module.css'
import { useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'
import { Spin, Tooltip } from 'antd'
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { nanoid } from 'nanoid'
import { CheckBoxLabel } from '@UI/CheckBox'
import CheckboxLabel from '@pages/Admin/CourseSettings/components/CheckboxLabel/CheckboxLabel'
import common from '../../index.module.css'; 

export const UploadMediaModal = ({
  data,
  closeModal = () => {},
}) => {
  if (data?.file) {
    return (
      <UpdateMediaModal
        file={data?.file}
        closeModal={closeModal}
        onSuccess={data?.onSuccess}
      />
    )
  } else {
    return <UploadMedia closeModal={closeModal} onSuccess={data?.onSuccess} />
  }
}

function createBlockData() {
  return {
    id: nanoid(),
    isUploaded: false,
    inProgress: false,
    progress: 0,
    files: [],
    name: '',
  }
}

const UploadMedia = ({ closeModal = () => {}, onSuccess = (data) => {} }) => {
  const profile = useSelector(selectProfile)
  const addAlert = useAddAlert()
  const [files, setFiles] = useState([])
  const [loadingAll, setLoadingAll] = useState(false)

  const uploadFile = (item) => {
    return new Promise((resolve, reject) => {
      setFiles((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, inProgress: true } : i)),
      )

      const data = new FormData()
      data.append('UserFilesForm[name]', item?.name)
      data.append('UserFilesForm[user_id]', profile?.id)
      item?.files.forEach((file) => data.append('file', file))
      data.append('compress', item?.compress ?? false)

      usersAPI
        .uploadPlatformMedia(data, {
          onUploadProgress: function (progressEvent) {
            let percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            )
            setFiles((prev) =>
              prev.map((i) =>
                i.id === item.id ? { ...i, progress: percentCompleted } : i,
              ),
            )
          },
        })
        .then((res) => {
          if (res.data.status === 'success') {
            addAlert({ title: 'Файл загружен' })
            setFiles((prev) =>
              prev.map((i) =>
                i.id === item.id
                  ? { ...i, inProgress: false, isUploaded: true }
                  : i,
              ),
            )
            resolve()
          } else {
            throw new Error('Ошибка загрузки')
          }
        })
        .catch((error) => {
          setFiles((prev) =>
            prev.map((i) =>
              i.id === item.id ? { ...i, inProgress: false } : i,
            ),
          )
          addAlert({
            title: 'Ошибка',
            text: 'Ошибка при загрузке файла. Попробуйте ещё раз.',
            type: 'error',
          })
          reject(error)
        })
    })
  }

  const uploadAllFiles = async () => {
    setLoadingAll(true)
    if (files.every((file) => file.isUploaded)) {
      addAlert({ title: 'Все файлы загружены!' })
      onSuccess()
      closeModal()
    }

    for (const file of files) {
      if (!file.isUploaded && !file.inProgress) {
        try {
          await uploadFile(file)
        } catch (error) {
          console.error('Ошибка загрузки файла:', error)
        }
      }
    }
    setLoadingAll(false)
  }

  useEffect(() => {
    if (loadingAll) return
    if (files.length > 0 && files.every((file) => file.isUploaded)) {
      addAlert({ title: 'Все файлы загружены!' })
      onSuccess()
      closeModal()
    }
  }, [loadingAll])

  const canUploadAll = useMemo(() => {
    const allValid = files.every(
      (file) => file.name?.length > 0 && file.files?.length > 0,
    )

    const hasUnUploaded = files.some((file) => !file.inProgress)

    return allValid && hasUnUploaded
  }, [files])

  const fileInputRef = useRef(null)

  function handleFileSelect(event) {
    const files = event.target.files
    if (!files.length) return

    const newFiles = Array.from(files).map((file) => ({
      id: nanoid(),
      name: file.name,
      files: [file],
      isUploaded: false,
      inProgress: false,
      progress: 0,
    }))

    setFiles((prev) => [...prev, ...newFiles])
  }

  return (
    <div className={classNames(classes.modalContainer, common.modalContainer)}>
      <div className={common.modalHeader}>
        <span className={common.modalHeaderBigTitle}>
          Загрузить файлы на платформу
        </span>
      </div>
      <div className={classes.uploadContainer}>
        {
          !!files?.length && (
            <div className={classes.fileList}>
              {files.map((item) => {
                return (
                  <UploadMediaBlock
                    key={item.id}
                    file={item}
                    onSetFile={(files) =>
                      setFiles((prev) =>
                        prev.map((i) => (i.id === item.id ? { ...i, files } : i)),
                      )
                    }
                    onSetName={(name) =>
                      setFiles((prev) =>
                        prev.map((i) => (i.id === item.id ? { ...i, name } : i)),
                      )
                    }
                    onCompress={(value) =>
                      setFiles((prev) =>
                        prev.map((i) =>
                          i.id === item.id ? { ...i, compress: value } : i,
                        ),
                      )
                    }
                    onRemove={() =>
                      setFiles((prev) => prev.filter((i) => i?.id !== item.id))
                    }
                    onUpload={() => uploadFile(item)}
                  />
                )
              })}
            </div>
          )
        }
        
        {files?.length === 0 && (
          <div className={classes.labelDescription}>
            Выберите файлы которые нужно сохранить на платформе.
          </div>
        )}
        <input
          type="file"
          multiple
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <button
          className={classes.secondaryButton}
          onClick={() => fileInputRef.current.click()}
        >
          Добавить файлы
        </button>
      </div>
      <div className={classes.footerActions}>
        <button
          className={classes.primaryButton}
          onClick={uploadAllFiles}
          disabled={loadingAll || !canUploadAll}
        >
          {loadingAll ? 'Сохранение' : 'Сохранить все'}
          {loadingAll && (
            <Spin size="small" indicator={<LoadingOutlined spin />} />
          )}
        </button>
      </div>
    </div>
  )
}

const UploadMediaBlock = ({
  file,
  onSetFile,
  onSetName,
  onCompress,
  onRemove,
  onUpload,
}) => {
  const addAlert = useAddAlert()

  const handleFileChange = (files) => {
    if (!files.length) {
      onSetFile(null)
      return
    }

    const filteredFiles = files.filter((file) => {
      const fileExtension = file.name.split('.').pop().toLowerCase()
      return !['mov'].includes(fileExtension)
    })

    if (filteredFiles.length !== files.length) {
      addAlert({
        title: 'Ошибка',
        text: 'Формат файла .mov не поддерживается.',
      })
    }

    onSetFile(filteredFiles.slice(0, 1))
  }

  const [bindFile] = useInputNew({
    value: file?.files ?? [],
    onChange: handleFileChange,
    multiple: true,
  })

  const isDisabled = file?.inProgress || file?.isUploaded

  return (
    <div className={classes.fileBlockContainer}>
      <input
        className={classes.customInput}
        name="name"
        placeholder="Название файла"
        value={file?.name ?? ''}
        onChange={(e) => onSetName(e.target.value)}
        disabled={isDisabled}
      />
      {!isDisabled && (
        <InputFile className={classes.inputMedia} {...bindFile} />
      )}
      <div className={classes.fileBlockActions}>
        {file?.files?.[0]?.type?.startsWith('video/') && (
          <CheckboxLabel
            label="Сжать"
            value={file?.compress ?? false}
            onChange={onCompress}
          />
        )}
        <button
          className={classNames(
            classes.secondaryButton,
            classes.fileBlockSaveButton,
          )}
          disabled={
            file?.files?.length < 1 || isDisabled || file?.name?.length < 1
          }
          onClick={onUpload}
        >
          {file?.isUploaded && 'Загружено'}
          {!file?.inProgress && !file?.isUploaded && 'Сохранить'}
          {file?.inProgress && (
            <Spin size="small" indicator={<LoadingOutlined spin />} />
          )}
          {file?.inProgress && `${file?.progress}%`}
        </button>
        {!file?.inProgress && !file?.isUploaded && (
          <button className={classes.removeBlockButton} onClick={onRemove}>
            <CloseOutlined />
          </button>
        )}
      </div>
    </div>
  )
}

const UpdateMediaModal = ({
  file = null,
  closeModal = () => {},
  onSuccess = (data) => {},
}) => {
  const profile = useSelector(selectProfile)
  const [fileName, setFileName] = useState(file?.name ?? '')
  const [progress, setProgress] = useState(0)
  const [loading, setLoading] = useState(false)
  const [updateRelated, setUpdateRelated] = useState(false)
  const [compressFile, setCompressFile] = useState(false)

  const addAlert = useAddAlert()
  const [sendButtonDisabled, setSendButtonDisabled] = useState(true)
  const [files, setFiles] = useState([])

  const handleFileChange = (files) => {
    if (!files.length) {
      setFiles(null)
      return
    }

    const filteredFiles = files.filter((file) => {
      const fileExtension = file.name.split('.').pop().toLowerCase()
      return !['mov'].includes(fileExtension)
    })

    if (filteredFiles.length !== files.length) {
      addAlert({
        title: 'Ошибка',
        text: 'Формат файла .mov не поддерживается.',
      })
    }

    setFiles(filteredFiles.slice(0, 1))
  }

  const [bindFile] = useInputNew({
    value: files,
    onChange: handleFileChange,
    multiple: true,
  })

  useEffect(() => {
    if (file == null) {
      setSendButtonDisabled(files?.length < 1)
    } else {
      setSendButtonDisabled(false)
    }
  }, [files])

  const onSubmit = (e) => {
    e.preventDefault()
    setLoading(true)

    const data = new FormData()
    data.append('UserFilesForm[name]', fileName)
    data.append('UserFilesForm[user_id]', profile?.id)
    data.append('UserFilesForm[update_related]', updateRelated)
    files.forEach((file) => data.append('file', file))
    data.append('UserFilesForm[id]', file?.id)
    data.append('compress', compressFile ?? false)

    const handleResponse = (res, successMessage) => {
      if (res.data.status === 'success') {
        addAlert({ title: successMessage })
        onSuccess(res.data)
        closeModal()
      } else {
        addAlert({
          title: 'Ошибка',
          text: 'Упс! Ошибка. Попробуйте ещё раз или обратитесь к администратору',
          type: 'error',
        })
      }
    }

    const handleError = (error) => {
      console.error('Ошибка при отправке:', error)
      addAlert({
        title: 'Ошибка',
        text: 'Что-то пошло не так. Пожалуйста, попробуйте ещё раз.',
        type: 'error',
      })
    }

    usersAPI
      .updatePlatformMedia(data, {
        onUploadProgress: function (progressEvent) {
          let percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          )
          setProgress(percentCompleted)
        },
      })
      .then((res) => handleResponse(res, 'Файл обновлен'))
      .catch(handleError)
      .finally(() => {
        setTimeout(() => {
          setLoading(false)
          setProgress(0)
        }, 1000)
      })
  }

  return (
    <div className={classNames(classes.modalContainer, common.modalContainer)}>
      <div className={common.modalHeader}>
        <span className={common.modalHeaderBigTitle}>
          {file ? 'Обновить файл на платформе' : 'Добавить файл на платформу'}
        </span>
      </div>
      <div style={{ background: 'white' }}>
        <div className={classes.mediaNameContainer}>
          <span className={classes.mediaNameTitle}>Введите название файла</span>
          <input
            className={classes.customInput}
            name="name"
            placeholder="Название"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
          />
        </div>
        <InputFile className={classes.inputMedia} {...bindFile} />
        {(!files?.[0]
          ? file?.type === 'Видео'
          : files?.[0]?.type?.startsWith('video/')) && (
          <CheckBoxLabel
            className={classes.checkbox}
            value={compressFile}
            onClick={() => setCompressFile((prev) => !prev)}
            label="Сжать файл"
          />
        )}
        <CheckBoxLabel
          className={classes.checkbox}
          value={updateRelated}
          onClick={() => setUpdateRelated((prev) => !prev)}
          label="Обновить название файла внутри курса"
        />
        <div className={classes.footerActions}>
          <Button
            className={classes.primaryButton}
            name={'Обновить'}
            newDesign
            action={onSubmit}
            disable={sendButtonDisabled || loading || fileName?.length < 1}
          />
          {loading && (
            <Spin size="medium" indicator={<LoadingOutlined spin />} />
          )}
        </div>
      </div>
    </div>
  )
}
